import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, APP_STATUS_LABELS } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import CancelPlacementModal from "@/components/CancelPlacementModal";
import { EmptyState, ErrorState } from "@/components/StateViews";
import { Loader2, ArrowLeft, ArrowRight, ClipboardList, FileSignature, CheckCircle2, Info, AlertTriangle, Ban } from "lucide-react";

export default function MyApplications() {
  const [apps, setApps] = useState([]);
  const [cancellations, setCancellations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cancelFor, setCancelFor] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [appsResp, cancResp] = await Promise.all([
        api.get("/applications/mine"),
        api.get("/placement-cancellations/mine").catch(() => ({ data: { cancellations: [] } })),
      ]);
      setApps(appsResp.data.applications || []);
      setCancellations(cancResp.data.cancellations || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const pendingCancelByAppId = new Map(
    cancellations.filter((c) => c.status === "pending").map((c) => [c.application_id, c])
  );

  return (
    <DashboardLayout>
      <div data-testid="my-applications">
        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-widest text-teal-700">طلباتي</div>
          <h1 className="mt-2 text-2xl font-heading font-extrabold text-slate-900">حالة طلباتك</h1>
        </div>

        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
        ) : error ? (
          <ErrorState message={error} onRetry={load} testid="my-apps-error" />
        ) : apps.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title="لم تقدم على أي فرصة بعد"
            description="ابدأ باستكشاف الفرص المتاحة والتقديم عليها."
            testid="my-apps-empty"
            action={
              <Link to="/dashboard/browse-opportunities" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 transition-colors">
                استكشف الفرص <ArrowLeft className="h-4 w-4" />
              </Link>
            }
          />
        ) : (
          <div className="space-y-4">
            {apps.map((a) => (
              <div key={a.id} className="p-4 bg-white rounded-2xl border border-slate-200 card-shadow flex flex-col sm:flex-row gap-6" data-testid={`app-item-${a.id}`}>
                {a.opportunity?.cover_image_url && (
                  <img src={a.opportunity.cover_image_url} alt="" className="h-24 w-32 object-cover rounded-xl shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-heading font-bold text-lg text-slate-900">{a.opportunity?.title || "فرصة"}</div>
                      <div className="text-sm text-slate-500 mt-1">{a.opportunity?.program_name}</div>
                    </div>
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold shrink-0
                      ${a.status === "accepted" || a.status === "assigned" ? "bg-emerald-50 text-emerald-700"
                        : a.status === "rejected" ? "bg-red-50 text-red-700"
                        : a.status === "under_review" ? "bg-sky-50 text-sky-700"
                        : "bg-amber-50 text-amber-700"}`}>
                      {APP_STATUS_LABELS[a.status]}
                    </span>
                  </div>
                  {a.motivation && (
                    <div className="mt-3 text-sm text-slate-600 line-clamp-2">{a.motivation}</div>
                  )}
                  <div className="mt-3 text-xs text-slate-500">
                    قُدّم في: {new Date(a.created_at).toLocaleDateString("ar-SA")}
                  </div>
                  <CommitmentCTA app={a} />
                  {["accepted", "assigned"].includes(a.status) && (
                    <div className="mt-3">
                      {pendingCancelByAppId.get(a.id) ? (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2" data-testid={`cancel-pending-${a.id}`}>
                          <Info className="h-4 w-4 shrink-0 mt-0.5" />
                          <span>طلب إلغاء التسكين قيد مراجعة الإدارة — سيتم إشعارك فور اتخاذ القرار.</span>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setCancelFor(a)}
                          className="inline-flex items-center gap-1.5 min-h-11 rounded-xl border border-rose-200 bg-rose-50 px-4 text-xs font-bold text-rose-700 hover:bg-rose-100"
                          data-testid={`request-cancel-${a.id}`}
                        >
                          <Ban className="h-3.5 w-3.5" /> طلب إلغاء التسكين
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {cancelFor && (
        <CancelPlacementModal
          app={cancelFor}
          onClose={() => setCancelFor(null)}
          onSubmitted={() => { setCancelFor(null); load(); }}
        />
      )}
    </DashboardLayout>
  );
}

function CommitmentCTA({ app }) {
  const status = app.commitment_status;
  const docId = app.commitment_document_id;
  if (!status) return null;

  if (status === "AWAITING_STUDENT_SIGNATURE" && docId) {
    return (
      <Link
        to={`/dashboard/contracts?document=${docId}`}
        className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 hover:bg-amber-100"
        data-testid={`app-commitment-cta-sign-${app.id}`}
      >
        <span className="flex items-center gap-2 font-bold">
          <FileSignature className="h-4 w-4" />
          وثيقة التزام التدريب التعاوني بانتظار توقيعك
        </span>
        <ArrowLeft className="h-4 w-4" />
      </Link>
    );
  }

  if (status === "SIGNED" && docId) {
    return (
      <Link
        to={`/dashboard/contracts?document=${docId}`}
        className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 hover:bg-emerald-100"
        data-testid={`app-commitment-cta-signed-${app.id}`}
      >
        <span className="flex items-center gap-2 font-bold">
          <CheckCircle2 className="h-4 w-4" />
          تم توقيع وثيقة التزام التدريب التعاوني
        </span>
        <span className="flex items-center gap-1 text-xs font-semibold">
          عرض التعهد <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </Link>
    );
  }

  if (status === "FINALIZING") {
    return (
      <div
        className="mt-4 flex items-center gap-2 rounded-xl border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900"
        data-testid={`app-commitment-cta-finalizing-${app.id}`}
      >
        <Info className="h-4 w-4" /> جارٍ تجهيز التعهد.
      </div>
    );
  }

  if (status === "FINALIZATION_FAILED") {
    return (
      <div
        className="mt-4 flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900"
        data-testid={`app-commitment-cta-failed-${app.id}`}
      >
        <AlertTriangle className="h-4 w-4" /> تحتاج الوثيقة معالجة من إدارة المنصة.
      </div>
    );
  }

  return null;
}
